import React, { useState, useEffect, useCallback } from 'react';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { CRow, CCol, CBadge } from '@coreui/react';
import CLabel from '../../../../../migration/CLabel';

import { callTokenApi } from '../../../../../../apiCaller';
import { formatDate, toastError } from '../../../../../../utils';
import { ADMIN_COMPANY_IDENTIFIER_RETRIEVE, DEFAULT_DATE_TIME_FORMAT, LLM_STATUS } from '../../../../../../constants';

const renderBoolean = (value, trueText = "Yes", falseText = "No", undefinedText = "none") => {
    if (typeof value === 'boolean') {
        return value ? <CBadge color="success" shape="rounded-pill">{trueText}</CBadge> : <CBadge color="danger" shape="rounded-pill">{falseText}</CBadge>;
    }
    return <CBadge color="secondary" shape="rounded-pill">{undefinedText}</CBadge>;
};

const renderJsonData = (jsonData, fieldName = "Data") => {
    if (jsonData === null || jsonData === undefined) return <small className="text-muted">none</small>;
    if (typeof jsonData === 'object') {
        if (Array.isArray(jsonData) && jsonData.length === 0) return <small className="text-muted">Empty</small>;
        return (
            <details className="json-details mb-2">
                <summary style={{ cursor: 'pointer', fontWeight: '500' }}>View {fieldName} (JSON)</summary>
                <pre style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    background: '#f8f9fa',
                    padding: '10px',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}>
                    {JSON.stringify(jsonData, null, 2)}
                </pre>
            </details>
        );
    }
    return String(jsonData);
};

const getStatusBadgeColor = (status) => {
    if (!status) return 'light';
    const lowerStatus = String(status).toLowerCase();
    if (lowerStatus.includes('valid') || lowerStatus.includes('processed') || lowerStatus.includes('enriched') || lowerStatus.includes('active') || lowerStatus.includes('matched')) return 'success';
    if (lowerStatus.includes('invalid') || lowerStatus.includes('failed') || lowerStatus.includes('error')) return 'danger';
    if (lowerStatus.includes('pending') || lowerStatus.includes('unverified') || lowerStatus.includes('unknown') || lowerStatus.includes('prospect') || lowerStatus.includes('tentative')) return 'warning';
    return 'secondary';
};

const CompanyIdentifier = ({ attributes }) => {
    const { id: personId } = attributes;

    const [identifierData, setIdentifierData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!personId) {
            setError("Person ID is missing.");
            setIsLoading(false);
            setIdentifierData(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await callTokenApi(`${ADMIN_COMPANY_IDENTIFIER_RETRIEVE.replace(':id', personId)}`, 'GET', null);

            if (response.status === 200 && response.data?.success) {
                setIdentifierData(response.data.data);
            } else {
                const errorMsg = response.data?.message || `Failed to fetch person data (status: ${response.status})`;
                setError(errorMsg);
                toastError({ data: { message: errorMsg } });
                setIdentifierData(null);
            }
        } catch (err) {
            console.error("API call failed for person detail:", err);
            const errorMessage = err.message || 'An unexpected error occurred.';
            setError(errorMessage);
            toastError({ data: { message: errorMessage } });
            setIdentifierData(null);
        } finally {
            setIsLoading(false);
        }
    }, [personId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (isLoading) {
        return <CenterSpinner />;
    }

    if (error) {
        return <div className="alert alert-danger" role="alert">Error: {error}</div>;
    }

    if (!identifierData) {
        return <div className="alert alert-info">No person data found for ID: {personId}</div>;
    }

    const { llmStatus } = identifierData;

    const fieldsToDisplay = [
        // Basic Information
        { label: "Account ID", value: identifierData.accountId || "none" },
        { label: "Visitor ID", value: identifierData.visitorId || "none" },
        { label: "Session ID", value: identifierData.sessionId || "none" },
        { label: "Company ID", value: identifierData.companyId || "none" },
        { label: "Type", value: identifierData.type },
        { label: "Value", value: identifierData.value, type: 'multiline' },
        { label: "Action", value: identifierData.action },

        // Source Information
        { label: "Source", value: identifierData.source },
        { label: "Source Path", value: identifierData.sourcePath },

        // Validation Section
        { label: "Valid Status", value: identifierData.validStatus, type: 'statusBadge' },
        { label: "Validation Run", value: renderBoolean(identifierData.validRun, "Run", "Not Run") },
        { label: "Validated At", value: formatDate(identifierData.validAt, DEFAULT_DATE_TIME_FORMAT) },
        { label: "Validation Data", value: renderJsonData(identifierData.validData, "Validation Data") },

        // Enrichment Section
        { label: "Enrich Status", value: identifierData.enrichStatus, type: 'statusBadge' },
        { label: "Enrichment Run", value: renderBoolean(identifierData.enrichRun, "Run", "Not Run") },
        { label: "Enriched At", value: formatDate(identifierData.enrichAt, DEFAULT_DATE_TIME_FORMAT) },
        { label: "Enrichment Data", value: renderJsonData(identifierData.enrichData, "Enrichment Data") },

        // LLM Section
        { label: "LLM Status", value: renderBoolean(llmStatus === LLM_STATUS.GOOD, llmStatus, llmStatus), type: 'statusBadge' },
        { label: "LLM Run", value: renderBoolean(identifierData.llmRun, "Run", "Not Run") },
        { label: "LLM Processed At", value: formatDate(identifierData.llmAt, DEFAULT_DATE_TIME_FORMAT) },
        { label: "LLM Data", value: renderJsonData(identifierData.llmData, "LLM Data") },

        // Company Status
        { label: "Company Status", value: identifierData.companyStatus, type: 'statusBadge' },

        { label: "Created At", value: formatDate(identifierData.created_at, DEFAULT_DATE_TIME_FORMAT) },
        { label: "Updated At", value: formatDate(identifierData.updated_at, DEFAULT_DATE_TIME_FORMAT) }
    ];

    const renderFieldValue = (field) => {
        if (field.type === 'multiline' && field.value) {
            return <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', fontSize: 'inherit' }}>{field.value}</pre>;
        }
        if (field.type === 'statusBadge' && field.value) {
            return <CBadge color={getStatusBadgeColor(field.value)} shape="rounded-pill">{field.value}</CBadge>;
        }
        if (typeof field.value === 'object' && field.value !== null && !React.isValidElement(field.value)) {
            return JSON.stringify(field.value);
        }
        if (React.isValidElement(field.value)) {
            return field.value;
        }
        return field.value ?? <small className="text-muted">none</small>;
    };

    return (
        <div className="rule-detail">
            <h3 className='mb-4'>
                Company Identifier Details: {identifierData.firstName || ''} {identifierData.lastName || ''}
            </h3>

            <CRow>
                {fieldsToDisplay.map(field => (
                    <React.Fragment key={field.label}>
                        <CCol md="3" className="mb-2">
                            <CLabel className="fw-semibold">{field.label}:</CLabel>
                        </CCol>

                        <CCol md="9" className="mb-2">
                            {renderFieldValue(field)}
                        </CCol>
                    </React.Fragment>
                ))}
            </CRow>
        </div>
    );
};

export default CompanyIdentifier;